import { Delete, FlipCameraAndroid, MoreVert, Visibility, VisibilityOff } from "@mui/icons-material";
import {
    Box,
    css,
    IconButton,
    IconButtonProps,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    styled,
    useTheme,
} from "@mui/material";
import { View } from "@novorender/api";
import { vec2 } from "gl-matrix";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { getClippingPlaneAnchorId, useClippingPlaneActions } from "features/clippingPlanes/useClippingPlaneActions";
import { RenderState, selectClippingPlanes } from "features/render";
import { useRedirectWheelEvents } from "hooks/useRedirectWheelEvents";
import { hasMouseSupport } from "utils/misc";

import { useSectionPlaneDrag } from "./hooks/useSectionPlaneDrag";
import {
    clearSectionPlaneCreateOrigin,
    showSectionPlaneMenuButtonAtPoint,
    useSectionPlaneCreateOrigin,
} from "./sectionPlaneCreateOrigin";
import { useSectionPlaneUndoRedo } from "./sectionPlaneUndoRedo";
import { getSectionPlaneArrowAngle, getSectionPlanePoints } from "./sectionPlaneUtils";

const dummyPoint = vec2.fromValues(-100, -100);

const POSITION_CHANGE_EPSILON = 0.5;
const MENU_VISIBILITY_SQR_DIST = 40 * 40;
const SECTION_PLANE_HUD_X_OFFSET = 17;

function isPointerNearSectionPlaneHud(point: vec2 | undefined, pointerPos: vec2 | null) {
    if (!point || !pointerPos) {
        return false;
    }

    const dx = point[0] + SECTION_PLANE_HUD_X_OFFSET - pointerPos[0];
    const dy = point[1] - pointerPos[1];

    return dx * dx + dy * dy <= MENU_VISIBILITY_SQR_DIST;
}

function shouldShowSectionPlaneMenuButton(params: {
    hasMouse: boolean;
    isMenuOpen: boolean;
    hasPendingMenuPoint: boolean;
    point: vec2 | undefined;
    pointerPos: vec2 | null;
}) {
    const { hasMouse, isMenuOpen, hasPendingMenuPoint, point, pointerPos } = params;
    return !hasMouse || isMenuOpen || hasPendingMenuPoint || isPointerNearSectionPlaneHud(point, pointerPos);
}

function setAnchorNodePosition(node: HTMLElement, point?: vec2) {
    const nextPoint = point ?? dummyPoint;
    node.style.setProperty("left", `${nextPoint[0]}px`);
    node.style.setProperty("top", `${nextPoint[1]}px`);
}

function hasAnchorMoved(node: HTMLElement, point: vec2) {
    const left = Number.parseFloat(node.style.left);
    const top = Number.parseFloat(node.style.top);

    if (!Number.isFinite(left) || !Number.isFinite(top)) {
        return true;
    }

    return Math.abs(left - point[0]) > POSITION_CHANGE_EPSILON || Math.abs(top - point[1]) > POSITION_CHANGE_EPSILON;
}

function setMenuButtonVisibility(node: HTMLElement, visible: boolean) {
    const menuButton = node.querySelector("[data-section-plane-menu-button]") as HTMLElement | null;
    if (!menuButton) {
        return;
    }

    menuButton.style.setProperty("opacity", visible ? "1" : "0");
    menuButton.style.setProperty("translate", visible ? "0" : "-20px");
    menuButton.style.setProperty("pointer-events", visible ? "auto" : "none");
}

function updateAnchorElements(params: {
    view: View;
    clipping: RenderState["clipping"];
    container: HTMLElement;
    openMenuIndex: number | null;
    closeMenu: () => void;
    lastPointerPos: vec2 | null;
    pendingCreateIndex: number | null;
    hasMouse: boolean;
}) {
    const { view, clipping, container, openMenuIndex, closeMenu, lastPointerPos, pendingCreateIndex, hasMouse } =
        params;
    const points = getSectionPlanePoints(view, clipping.planes);
    const pointByIndex = new Map(points.map(({ index, point }) => [index, point]));

    if (openMenuIndex !== null && !pointByIndex.get(openMenuIndex)) {
        closeMenu();
    }

    clipping.planes.forEach((plane, index) => {
        const node = container.querySelector(`#${getClippingPlaneAnchorId(index)}`) as HTMLElement | null;
        if (!node) {
            return;
        }

        const point = pointByIndex.get(index);
        if (!point) {
            if (pendingCreateIndex !== index) {
                setAnchorNodePosition(node, dummyPoint);
            }
            setMenuButtonVisibility(node, !hasMouse || openMenuIndex === index || pendingCreateIndex === index);
            return;
        }

        if (openMenuIndex === index && hasAnchorMoved(node, point)) {
            closeMenu();
        }

        setAnchorNodePosition(node, point);
        setMenuButtonVisibility(
            node,
            shouldShowSectionPlaneMenuButton({
                hasMouse,
                isMenuOpen: openMenuIndex === index,
                hasPendingMenuPoint: pendingCreateIndex === index,
                point,
                pointerPos: lastPointerPos,
            }),
        );

        const arrow = node.querySelector("[data-section-plane-arrow]") as HTMLElement | null;
        if (arrow) {
            arrow.style.transform = `rotate(${getSectionPlaneArrowAngle(view, plane)}deg)`;
        }
    });
}

/** Renders Section Plane double-arrow that gets CSS-rotated to match the plane's actual movement direction on screen. */
function SectionPlaneArrow() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="24"
            fill="none"
            viewBox="0 0 24 40"
            style={{ display: "block" }}
            data-section-plane-arrow
        >
            <path fill="#232729" d="m12.392 0 10.393 18-10.393-5.5L2 18zm0 40L2 22l10.392 5.5L22.784 22z" />
        </svg>
    );
}

/**
 * Renders interactive anchors for section planes.
 * Each anchor can be dragged to move the plane and has a context menu for plane operations.
 */
export const SectionPlaneInteractions = forwardRef(function SectionPlaneInteractions(_props, ref) {
    const {
        state: { view, canvas },
    } = useExplorerGlobals(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const clipping = useAppSelector(selectClippingPlanes);
    const clippingRef = useRef(clipping);
    clippingRef.current = clipping;
    const actions = useClippingPlaneActions();
    const undoRedo = useSectionPlaneUndoRedo();
    const onWheel = useRedirectWheelEvents();
    const hasMouse = hasMouseSupport();
    const { movingPlaneIndex, handlePointerDown } = useSectionPlaneDrag({
        view,
        clipping,
        actions,
        undoRedo,
        onTapPlane: (index, screenPoint) => {
            clearSectionPlaneCreateOrigin();
            showSectionPlaneMenuButtonAtPoint(index, screenPoint);
        },
    });
    const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
    const openMenuIndexRef = useRef<number | null>(openMenuIndex);
    openMenuIndexRef.current = openMenuIndex;
    const prevClippingRef = useRef(clipping);
    const lastPointerPosRef = useRef<vec2 | null>(null);
    const pendingCreateOrigin = useSectionPlaneCreateOrigin();
    const pendingCreateIndex = pendingCreateOrigin?.index ?? null;

    const runAnchorUpdate = useCallback(
        (lastPointerPos: vec2 | null = lastPointerPosRef.current) => {
            if (!view || !containerRef.current || movingPlaneIndex !== -1) {
                return;
            }
            updateAnchorElements({
                view,
                clipping: clippingRef.current,
                container: containerRef.current,
                openMenuIndex: openMenuIndexRef.current,
                closeMenu: () => setOpenMenuIndex(null),
                lastPointerPos,
                pendingCreateIndex,
                hasMouse,
            });
        },
        [view, movingPlaneIndex, pendingCreateIndex, hasMouse],
    );

    // Always points to the latest runAnchorUpdate so pointer handlers never go stale.
    const runAnchorUpdateRef = useRef(runAnchorUpdate);
    runAnchorUpdateRef.current = runAnchorUpdate;

    const updatePointerPosition = useCallback(
        (clientX: number, clientY: number) => {
            if (!canvas || !hasMouse) {
                return;
            }

            const canvasRect = canvas.getBoundingClientRect();
            const nextPos = vec2.fromValues(clientX - canvasRect.left, clientY - canvasRect.top);
            clearSectionPlaneCreateOrigin();
            lastPointerPosRef.current = nextPos;
            runAnchorUpdateRef.current(nextPos);
        },
        [canvas, hasMouse],
    );

    useImperativeHandle(ref, () => ({ update: runAnchorUpdate }), [runAnchorUpdate]);

    useEffect(() => {
        if (!canvas || !hasMouse) {
            return;
        }

        const updateFromPointerEvent = (event: Event) => {
            if (!(event instanceof PointerEvent)) {
                return;
            }
            updatePointerPosition(event.clientX, event.clientY);
        };

        canvas.addEventListener("pointermove", updateFromPointerEvent);
        canvas.addEventListener("pointerdown", updateFromPointerEvent);

        return () => {
            canvas.removeEventListener("pointermove", updateFromPointerEvent);
            canvas.removeEventListener("pointerdown", updateFromPointerEvent);
        };
    }, [canvas, hasMouse, updatePointerPosition]);

    useEffect(() => {
        if (openMenuIndex !== null && openMenuIndex >= clipping.planes.length) {
            setOpenMenuIndex(null);
        }
        if (pendingCreateOrigin && pendingCreateOrigin.index >= clipping.planes.length) {
            clearSectionPlaneCreateOrigin();
        }
    }, [clipping.planes.length, openMenuIndex, pendingCreateOrigin]);

    useEffect(() => {
        const planesOrEnabledChanged =
            prevClippingRef.current.enabled !== clipping.enabled || prevClippingRef.current.planes !== clipping.planes;
        const clippingChanged = prevClippingRef.current !== clipping && planesOrEnabledChanged;

        if (openMenuIndex !== null && clippingChanged) {
            setOpenMenuIndex(null);
        }
        if (pendingCreateOrigin && planesOrEnabledChanged) {
            const targetPlane = clipping.planes[pendingCreateOrigin.index];
            if (!targetPlane?.anchorPos) {
                clearSectionPlaneCreateOrigin();
            }
        }
        prevClippingRef.current = clipping;
    }, [clipping, openMenuIndex, pendingCreateOrigin]);

    useLayoutEffect(() => {
        runAnchorUpdate();
    }, [clipping, clipping.planes.length, openMenuIndex, runAnchorUpdate]);

    if (!view) {
        return null;
    }

    const sectionPlanePoints = getSectionPlanePoints(view, clipping.planes);
    const pointByIndex = new Map(sectionPlanePoints.map(({ index, point }) => [index, point]));

    return (
        <Box ref={containerRef}>
            {clipping.planes.map((plane, index) => (
                <SectionPlaneBox
                    key={index}
                    index={index}
                    plane={plane}
                    view={view}
                    movingPlaneIndex={movingPlaneIndex}
                    handlePointerDown={handlePointerDown}
                    clipping={clipping}
                    point={pointByIndex.get(index)}
                    lastPointerPos={lastPointerPosRef.current}
                    pendingMenuPoint={pendingCreateOrigin?.index === index ? pendingCreateOrigin.point : null}
                    onWheel={onWheel}
                    isMenuOpen={openMenuIndex === index}
                    onOpenMenu={() => setOpenMenuIndex(index)}
                    onCloseMenu={() => setOpenMenuIndex((current) => (current === index ? null : current))}
                    onTrackPointer={updatePointerPosition}
                    hasMouse={hasMouse}
                />
            ))}
        </Box>
    );
});

function SectionPlaneBox({
    index,
    movingPlaneIndex,
    handlePointerDown,
    clipping,
    point,
    lastPointerPos,
    pendingMenuPoint,
    onWheel,
    isMenuOpen,
    onOpenMenu,
    onCloseMenu,
    onTrackPointer,
    hasMouse,
}: {
    index: number;
    plane: RenderState["clipping"]["planes"][number];
    view: View;
    movingPlaneIndex: number;
    handlePointerDown: (e: React.PointerEvent, index: number) => void;
    clipping: RenderState["clipping"];
    point: vec2 | undefined;
    lastPointerPos: vec2 | null;
    pendingMenuPoint: vec2 | null;
    onWheel: (evt: React.WheelEvent) => void;
    isMenuOpen: boolean;
    onOpenMenu: () => void;
    onCloseMenu: () => void;
    onTrackPointer: (clientX: number, clientY: number) => void;
    hasMouse: boolean;
}) {
    const theme = useTheme();
    const anchorBoxRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!anchorBoxRef.current) {
            return;
        }

        setAnchorNodePosition(anchorBoxRef.current, dummyPoint);
    }, []);

    useLayoutEffect(() => {
        if (!anchorBoxRef.current || !pendingMenuPoint) {
            return;
        }

        setAnchorNodePosition(anchorBoxRef.current, pendingMenuPoint);
    }, [pendingMenuPoint]);

    const isCloseHudVisible =
        movingPlaneIndex === -1 &&
        shouldShowSectionPlaneMenuButton({
            hasMouse,
            isMenuOpen,
            hasPendingMenuPoint: Boolean(pendingMenuPoint),
            point,
            pointerPos: lastPointerPos,
        });

    return (
        <Box
            ref={anchorBoxRef}
            sx={{
                position: "absolute",
                translate: "calc(-17px) calc(-17px)",
                display: "flex",
                gap: 0.5,
                alignItems: "center",
                opacity: movingPlaneIndex === -1 || movingPlaneIndex === index ? 1 : 0,
                pointerEvents: movingPlaneIndex === -1 || movingPlaneIndex === index ? "auto" : "none",
                transition: "opacity 0.1s",
            }}
            id={getClippingPlaneAnchorId(index)}
            onWheel={onWheel}
            onPointerEnter={
                hasMouse ? (event: React.PointerEvent) => onTrackPointer(event.clientX, event.clientY) : undefined
            }
            onPointerMove={
                hasMouse ? (event: React.PointerEvent) => onTrackPointer(event.clientX, event.clientY) : undefined
            }
        >
            <SectionPlaneMoveButton
                size="small"
                onPointerDown={(e) => handlePointerDown(e, index)}
                aria-label="Move section plane"
            >
                <SectionPlaneArrow />
            </SectionPlaneMoveButton>
            <SectionPlaneMenu
                clipping={clipping}
                index={index}
                visible={isCloseHudVisible}
                anchorEl={anchorBoxRef.current}
                isOpen={isMenuOpen}
                onOpen={() => {
                    clearSectionPlaneCreateOrigin();
                    onOpenMenu();
                }}
                onClose={() => {
                    clearSectionPlaneCreateOrigin();
                    onCloseMenu();
                }}
            />
            <Box
                data-offset
                sx={{
                    position: "absolute",
                    left: 0,
                    top: -40,
                    background: theme.palette.background.paper,
                    px: 0.5,
                    borderRadius: 1,
                    fontSize: "small",
                    color: "grey",
                    display: "none",
                    whiteSpace: "nowrap",
                    translate: "calc(17px - 50%)",
                }}
            >
                Offset
            </Box>
        </Box>
    );
}

function SectionPlaneMenu({
    clipping,
    index,
    visible,
    anchorEl,
    isOpen,
    onOpen,
    onClose,
}: {
    clipping: RenderState["clipping"];
    index: number;
    visible: boolean;
    anchorEl: HTMLElement | null;
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const { showClippingPlaneWithUndo, flipClippingPlaneWithUndo, deleteClippingPlaneWithUndo } =
        useSectionPlaneUndoRedo();
    const plane = clipping.planes[index];

    const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onOpen();
    };

    const handleShowPlane = () => {
        showClippingPlaneWithUndo(index, !plane.showPlane);
        onClose();
    };

    const handleFlipDirection = () => {
        flipClippingPlaneWithUndo(index);
        onClose();
    };

    const handleDelete = () => {
        deleteClippingPlaneWithUndo(index);
        onClose();
    };

    visible = visible || isOpen;

    return (
        <>
            <StyledMenuButton
                size="small"
                onClick={openMenu}
                aria-label="Section plane menu"
                data-section-plane-menu-button
                sx={{
                    opacity: visible ? 1 : 0,
                    transition: "opacity 0.1s, translate 0.1s",
                    translate: visible ? 0 : "-20px",
                    pointerEvents: visible ? "auto" : "none",
                }}
            >
                <MoreVert fontSize="inherit" />
            </StyledMenuButton>

            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={onClose}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem onClick={handleShowPlane}>
                    <ListItemIcon>
                        {plane.showPlane ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText>{t(plane.showPlane ? "hidePlane" : "showPlane")}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleFlipDirection}>
                    <ListItemIcon>
                        <FlipCameraAndroid fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t("flipDirection")}</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDelete}>
                    <ListItemIcon>
                        <Delete fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t("delete")}</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}

const SectionPlaneMoveButton = styled(IconButton)<IconButtonProps>(
    ({ theme }) => css`
        background-color: ${theme.palette.background.paper};
        color: ${theme.palette.text.primary};
        opacity: 0.9;
        transition: opacity 0.2s;
        touch-action: none;
        width: 34px;
        height: 34px;
        overflow: visible;
        &:hover {
            background-color: ${theme.palette.grey["100"]};
            opacity: 1;
        }
    `,
);

const StyledMenuButton = styled(IconButton)(
    ({ theme }) => css`
        background-color: ${theme.palette.background.paper};
        color: ${theme.palette.text.primary};
        &:hover {
            background-color: ${theme.palette.grey["100"]};
        }
    `,
);
